import { useRef, useState } from "react";
import { FileTextIcon, LandmarkIcon, Trash2Icon, UploadIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { formatearPeso } from "./ZonaDeImagen";

/** Espejo de `GuardarArchivoDeAntecedenteRequest::MAXIMO_KB`. */
export const MAXIMO_BYTES_DEL_AVALUO = 32 * 1024 * 1024;

const TIPOS_ADMITIDOS = ["application/pdf", "image/jpeg", "image/png", "image/webp"];

/**
 * Lo que el navegador puede rechazar sin gastar la subida, o `null`. No es la
 * autoridad: la validación real mira los bytes en el servidor.
 */
export function motivoDeRechazoDelAvaluo(archivo: File): string | null {
  if (!TIPOS_ADMITIDOS.includes(archivo.type)) {
    return "El certificado debe ser un PDF o una imagen (JPG, PNG o WebP).";
  }
  if (archivo.size > MAXIMO_BYTES_DEL_AVALUO) {
    return "El archivo pesa más de 32 MB. Crea el informe y adjúntalo desde Antecedentes legales.";
  }
  return null;
}

/**
 * Espacio para subir el certificado de avalúo fiscal del SII al crear el informe.
 *
 * NO ES UN IMPORTADOR: no se lee con IA ni precarga nada, es un adjunto a secas.
 * Se retiene en el navegador y la pantalla de creación lo sube al tipo
 * `certificado-avaluo-sii` cuando el informe ya tiene id. Solo existe en el
 * alta, junto a {@see ImportadorDeTasacion}; la edición adjunta desde la
 * pestaña de antecedentes legales.
 *
 * Presentacional: no sabe de la API.
 */
export function AdjuntoDelAvaluoFiscal({
  archivo,
  onArchivo,
  onQuitar,
}: {
  archivo: File | null;
  onArchivo: (archivo: File) => void;
  onQuitar: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function elegir(elegido: File | undefined) {
    if (!elegido) return;
    const motivo = motivoDeRechazoDelAvaluo(elegido);
    setError(motivo);
    if (motivo === null) onArchivo(elegido);
  }

  return (
    <section className="bg-card space-y-4 rounded-xl border p-5">
      <div className="flex items-start gap-3">
        <LandmarkIcon className="text-primary mt-0.5 size-5 shrink-0" aria-hidden="true" />
        <div>
          <h2 className="text-h3 text-ink">Certificado de avalúo fiscal (SII)</h2>
          <p className="text-ink-muted text-caption">
            Se adjunta a los antecedentes legales del informe al crearlo. No se lee con IA.
          </p>
        </div>
      </div>

      {archivo === null ? (
        <div
          role="button"
          tabIndex={0}
          aria-label="Subir el certificado de avalúo fiscal"
          onClick={() => input.current?.click()}
          onKeyDown={(evento) => {
            if (evento.key === "Enter" || evento.key === " ") {
              evento.preventDefault();
              input.current?.click();
            }
          }}
          onDragOver={(evento) => {
            evento.preventDefault();
            setArrastrando(true);
          }}
          onDragLeave={() => setArrastrando(false)}
          onDrop={(evento) => {
            evento.preventDefault();
            setArrastrando(false);
            elegir(evento.dataTransfer.files?.[0]);
          }}
          className={cn(
            "flex cursor-pointer flex-col items-center gap-2 rounded-md border-[1.5px] border-dashed px-5 py-8 text-center transition-colors",
            "focus-visible:border-ring focus-visible:ring-ring/50 outline-none focus-visible:ring-[3px]",
            arrastrando ? "border-primary bg-primary/5" : "border-input hover:bg-muted/40",
          )}
        >
          <UploadIcon className="text-ink-hint size-6" aria-hidden="true" />
          <span className="text-ink text-sm font-semibold">
            Arrastra el archivo o haz clic para elegirlo
          </span>
          <span className="text-ink-hint text-xs">PDF o imagen de hasta 32 MB</span>
        </div>
      ) : (
        <div className="bg-muted/40 flex items-center gap-2 rounded-md px-3 py-2">
          <FileTextIcon className="text-ink-hint size-4 shrink-0" aria-hidden="true" />
          <span className="text-ink truncate text-sm font-semibold">{archivo.name}</span>
          <span className="text-ink-hint ml-auto shrink-0 font-mono text-xs">
            {formatearPeso(archivo.size)}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onQuitar}
            aria-label={`Quitar ${archivo.name}`}
          >
            <Trash2Icon aria-hidden="true" />
          </Button>
        </div>
      )}

      {/* Oculto y disparado por el recuadro, igual que el importador. */}
      <input
        ref={input}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.webp"
        className="sr-only"
        aria-label="Certificado de avalúo fiscal"
        onChange={(evento) => {
          const elegido = evento.target.files?.[0];
          // Se limpia siempre: volver a elegir el mismo archivo tras quitarlo
          // no dispararía `change`.
          evento.target.value = "";
          elegir(elegido);
        }}
      />

      {error ? <p className="text-destructive text-sm">{error}</p> : null}
    </section>
  );
}
